import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {hovercardAttributesForActor} from '@github-ui/hovercards'
import {ProfileReference} from '@github-ui/profile-reference'
import {Link} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {VALUES} from '../../constants/values'
import type {EventActor$key} from './__generated__/EventActor.graphql'
import {GhostActor} from './GhostActor'
import styles from './row.module.css'

type EventActorProps = {
  actor: EventActor$key | null
} & SharedProps

type EventActorPropsInternal = {
  actor: EventActor$key
} & SharedProps

type SharedProps = {
  showAvatarOnly: boolean
  isAgent?: boolean
  displayName?: string
}

type EventActorBaseProps = {
  login?: string
  profileName?: string | null
  avatarUrl: string
  profileResourcePath?: string
  isCopilot?: boolean
  isUser?: boolean
} & SharedProps

export function EventActor({actor, ...props}: EventActorProps): React.ReactElement {
  return actor ? <EventActorInternal actor={actor} {...props} /> : <GhostActor />
}

function EventActorInternal({actor, ...props}: EventActorPropsInternal): React.ReactElement {
  const {login, name, avatarUrl, isCopilot, profileResourcePath, __typename, isAgent, displayName} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment EventActor on Actor {
        avatarUrl(size: 64)
        login
        profileResourcePath
        __typename
        ... on Bot {
          isCopilot
          isAgent
          displayName
        }
        ... on User {
          name
        }
        ... on EnterpriseUserAccount {
          name
        }
        ... on Mannequin {
          name
        }
      }
    `,
    actor,
  )

  const isUser = ['User', 'Organization'].includes(__typename)

  return (
    <EventActorBase
      login={login}
      profileName={name}
      avatarUrl={avatarUrl}
      profileResourcePath={profileResourcePath ?? undefined}
      isCopilot={isCopilot}
      isUser={isUser}
      isAgent={isAgent}
      displayName={displayName}
      {...props}
    />
  )
}

function EventActorBase({
  login,
  profileName,
  avatarUrl,
  profileResourcePath,
  showAvatarOnly,
  isCopilot = false,
  isUser = true,
  isAgent = false,
  displayName = '',
}: EventActorBaseProps): React.ReactElement {
  if (login === VALUES.ghost.login) {
    return <span>{LABELS.repositoryOwner} </span>
  } else if (!login) {
    return <span>{VALUES.ghost.login} </span>
  }

  const useHovercard = isUser || isCopilot || isAgent
  const getActorDisplayName = () => {
    if (isCopilot) return VALUES.copilot.displayName
    if (isAgent && displayName) return displayName
    return login
  }
  const actorDisplayName = getActorDisplayName()

  return (
    <div className={styles.eventActorContainer}>
      <Link
        data-testid="actor-link"
        role="link"
        href={profileResourcePath}
        {...(useHovercard ? hovercardAttributesForActor(login, {isCopilot, isAgent}) : {})}
        className={styles.eventActorLink}
        muted
      >
        {isCopilot ? (
          <CopilotAvatar size={'small'} className={styles.alignSelfCenter} />
        ) : (
          <GitHubAvatar src={avatarUrl} size={16} className={styles.alignSelfCenter} />
        )}
        {!showAvatarOnly && (
          <span className={styles.eventProfileReference}>
            {isFeatureEnabled('render_user_display_name') ? (
              <ProfileReference login={actorDisplayName} profileName={profileName} />
            ) : (
              actorDisplayName
            )}
          </span>
        )}
      </Link>
    </div>
  )
}

try{ EventActor.displayName ||= 'EventActor' } catch {}
try{ EventActorInternal.displayName ||= 'EventActorInternal' } catch {}
try{ EventActorBase.displayName ||= 'EventActorBase' } catch {}